import React from "react";
import { isDevEnvironment } from "@/utils/environment";

// Whitelabel display labels for preview mode
export const WHITELABEL_LABELS: Record<string, string> = {
  sharebike: isDevEnvironment() ? "Shematic" : "Lisa",
  lisa: "LISA",
  bigIssue: "Bigissue",
  esb: "ESB",
  farteBysykkel: "Farte",
  sigo: "Sigo",
  sefa: "Sefa",
  weelz: "Weelz",
  gjovik: "Gjøvik",
  "lisa-demo": "LISA Demo",
};

// Lazy-loaded page containers for code splitting
export const ClientContainer = React.lazy(
  () => import("@/v2/containers/clientContainer")
);
export const BikeContainer = React.lazy(
  () => import("@/v2/containers/bikeContainer")
);
export const LocksContainer = React.lazy(
  () => import("@/v2/containers/locksContainer")
);
export const TransactionContainer = React.lazy(
  () => import("@/v2/containers/transactionContainer")
);
export const StationContainer = React.lazy(
  () => import("@/v2/containers/stationContainer")
);
export const DockContainer = React.lazy(
  () => import("@/v2/containers/dockContainer")
);
export const CityContainer = React.lazy(
  () => import("@/v2/containers/cityContainer")
);
export const OrganizationContainer = React.lazy(
  () => import("@/v2/containers/organizationContainer")
);
export const PartnersContainer = React.lazy(
  () => import("@/v2/containers/partnersContainer")
);
export const PartnerDetailContainer = React.lazy(
  () => import("@/v2/containers/partnerDetailContainer")
);
export const FinesContainer = React.lazy(
  () => import("@/v2/containers/finesContainer")
);
export const FineDetailContainer = React.lazy(
  () => import("@/v2/containers/fineDetailContainer")
);
export const VehicleModelsContainer = React.lazy(
  () => import("@/v2/containers/vehicleModelsContainer")
);
export const VehicleModelDetailContainer = React.lazy(
  () => import("@/v2/containers/vehicleModelDetailContainer")
);
export const RentalsContainer = React.lazy(
  () => import("@/v2/containers/rentalsContainer")
);
export const MapContainerV2 = React.lazy(
  () => import("@/v2/containers/mapContainerV2")
);
export const OverviewContainer = React.lazy(
  () => import("@/v2/containers/overviewContainer")
);
export const BikeHistoryContainer = React.lazy(
  () => import("@/v2/containers/bikeHistoryContainer")
);
export const ReferralsScreen = React.lazy(
  () => import("@/v2/containers/referralsContainer")
);
export const RewardsContainer = React.lazy(
  () => import("@/v2/containers/promotionsContainer/rewards")
);
export const TopUpBonusesContainer = React.lazy(
  () => import("@/v2/containers/promotionsContainer/topupBonuses")
);
export const RidePassContainer = React.lazy(
  () => import("@/v2/containers/ridePassContainer")
);
export const MFAContainer = React.lazy(
  () => import("@/v2/containers/MFAContainer")
);
export const UserLedger = React.lazy(
  () => import("@/v2/containers/userLedger")
);
export const PricingHub = React.lazy(
  () => import("@/v2/containers/pricingHub")
);
export const PricingPlanEditor = React.lazy(
  () => import("@/v2/containers/pricingPlanEditor")
);
export const UserManagementScreen = React.lazy(
  () => import("@/v2/containers/userManagementContainer")
);
export const CouponsScreen = React.lazy(
  () => import("@/v2/containers/couponsContainer")
);
export const CouponUserContainer = React.lazy(
  () => import("@/v2/containers/couponUserContainer")
);
export const SettingsContainer = React.lazy(
  () => import("@/v2/containers/SettingsContainer")
);
export const CustomerRestrictionsContainer = React.lazy(
  () => import("@/v2/containers/customerRestrictionsContainer")
);

export const NotificationsContainer = React.lazy(() =>
  import("@/v2/containers/notificationsContainer/NotificationsContainer").then(
    (m) => ({
      default: m.NotificationsContainer,
    })
  )
);

export const RentalRulesContainer = React.lazy(() =>
  import("@/v2/containers/rentalRules/RentalRulesContainer").then((m) => ({
    default: m.RentalRulesContainer,
  }))
);

export const RentalsGallery = React.lazy(() =>
  import("@/v2/containers/rentalsGallery/RentalsGallery").then((m) => ({
    default: m.RentalsGallery,
  }))
);

export const PricingPlanDomains = React.lazy(() =>
  import("@/v2/containers/pricingPlanDomains/PricingPlanDomains").then((m) => ({
    default: m.PricingPlanDomains,
  }))
);

export const ServiceAccountsContainer = React.lazy(() =>
  import("@/v2/containers/serviceAccounts/ServiceAccountsContainer").then(
    (m) => ({
      default: m.ServiceAccountsContainer,
    })
  )
);

export const IntrosContainer = React.lazy(() =>
  import("@/v2/containers/intros/IntrosContainer").then((m) => ({
    default: m.IntrosContainer,
  }))
);

export const WarningsContainer = React.lazy(() =>
  import("@/v2/containers/warnings/WarningsContainer").then((m) => ({
    default: m.WarningsContainer,
  }))
);

export const ReservationsContainer = React.lazy(() =>
  import("@/v2/containers/reservations/ReservationsContainer").then((m) => ({
    default: m.ReservationsContainer,
  }))
);

export const AnalyticsContainer = React.lazy(() =>
  import("@/v2/containers/analyticsContainer/AnalyticsContainer").then((m) => ({
    default: m.AnalyticsContainer,
  }))
);

export const SupersetDashboardContainer = React.lazy(() =>
  import(
    "@/v2/containers/supersetDashboardContainer/SupersetDashboardContainer"
  ).then((m) => ({
    default: m.SupersetDashboardContainer,
  }))
);

export const ChatAnalyticsContainer = React.lazy(() =>
  import("@/v2/containers/chatAnalyticsContainer/ChatAnalyticsContainer").then(
    (m) => ({
      default: m.ChatAnalyticsContainer,
    })
  )
);

export const AppVersionsContainer = React.lazy(() =>
  import("@/v2/containers/appVersionsContainer/AppVersionsContainer").then(
    (m) => ({
      default: m.AppVersionsContainer,
    })
  )
);

export const ServiceHealthContainer = React.lazy(() =>
  import("@/v2/containers/serviceHealthContainer/ServiceHealthContainer").then(
    (m) => ({
      default: m.ServiceHealthContainer,
    })
  )
);

export const ApiDocsContainer = React.lazy(() =>
  import("@/v2/containers/apiDocsContainer/ApiDocsContainer").then((m) => ({
    default: m.ApiDocsContainer,
  }))
);

export const AppConfigurationContainer = React.lazy(() =>
  import("@/v2/containers/appConfigurationContainer").then((m) => ({
    default: m.AppConfigurationContainer,
  }))
);

export const FeatureFlagsContainer = React.lazy(() =>
  import("@/v2/containers/featureFlagsContainer/FeatureFlagsContainer").then(
    (m) => ({
      default: m.FeatureFlagsContainer,
    })
  )
);

// Lazy-load OnboardingTour to defer react-joyride (~40KB) until needed
export const OnboardingTour = React.lazy(() =>
  import("@/v2/components/onboarding/OnboardingTour").then((m) => ({
    default: m.OnboardingTour,
  }))
);
